import type { Timeframe } from '../types/ohlcv';

/** Every timeframe, shortest first. */
export const TIMEFRAMES: readonly Timeframe[] = [
  '1m', '3m', '5m', '15m', '30m',
  '1h', '2h', '4h', '6h', '12h',
  '1D', '3D', '1W', '1M',
];

const TIMEFRAME_MS: Record<Timeframe, number> = {
  '1m': 60_000,
  '3m': 180_000,
  '5m': 300_000,
  '15m': 900_000,
  '30m': 1_800_000,
  '1h': 3_600_000,
  '2h': 7_200_000,
  '4h': 14_400_000,
  '6h': 21_600_000,
  '12h': 43_200_000,
  '1D': 86_400_000,
  '3D': 259_200_000,
  '1W': 604_800_000,
  // Calendar months vary; 30 days is used for windows and spacing estimates only.
  '1M': 2_592_000_000,
};

const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  '1m': '1 Minute',
  '3m': '3 Minutes',
  '5m': '5 Minutes',
  '15m': '15 Minutes',
  '30m': '30 Minutes',
  '1h': '1 Hour',
  '2h': '2 Hours',
  '4h': '4 Hours',
  '6h': '6 Hours',
  '12h': '12 Hours',
  '1D': '1 Day',
  '3D': '3 Days',
  '1W': '1 Week',
  '1M': '1 Month',
};

const TIMEFRAME_SHORT: Record<Timeframe, string> = {
  '1m': '1m',
  '3m': '3m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1H',
  '2h': '2H',
  '4h': '4H',
  '6h': '6H',
  '12h': '12H',
  '1D': 'D',
  '3D': '3D',
  '1W': 'W',
  '1M': 'M',
};

export function timeframeToMs(tf: Timeframe): number {
  return TIMEFRAME_MS[tf];
}

export function timeframeLabel(tf: Timeframe): string {
  return TIMEFRAME_LABELS[tf];
}

/** Compact label as shown on toolbars: 1m, 15m, 1H, 4H, D, W, M. */
export function timeframeShortLabel(tf: Timeframe): string {
  return TIMEFRAME_SHORT[tf];
}

export function isTimeframe(value: unknown): value is Timeframe {
  return typeof value === 'string' && value in TIMEFRAME_MS;
}

/**
 * Parse loose interval input — "15", "15m", "60", "1h", "4H", "D", "1d", "W", "M" —
 * into a Timeframe. Bare numbers are minutes (TradingView convention). Null when
 * nothing matches.
 */
export function parseTimeframe(input: string): Timeframe | null {
  const raw = input.trim();
  if (!raw) return null;
  if (isTimeframe(raw)) return raw;
  const m = /^(\d*)\s*([a-zA-Z]*)$/.exec(raw);
  if (!m) return null;
  const n = m[1] ? Number(m[1]) : 1;
  const unit = m[2];
  let candidate: string;
  if (unit === '' ) {
    if (n % 60 === 0 && n >= 60) candidate = `${n / 60}h`;
    else candidate = `${n}m`;
  } else if (unit === 'm' || unit === 'min') {
    candidate = `${n}m`;
  } else if (unit === 'h' || unit === 'H') {
    candidate = `${n}h`;
  } else if (unit === 'd' || unit === 'D') {
    candidate = `${n}D`;
  } else if (unit === 'w' || unit === 'W') {
    candidate = `${n}W`;
  } else if (unit === 'M' || unit === 'mo' || unit === 'mon') {
    candidate = `${n}M`;
  } else {
    return null;
  }
  return isTimeframe(candidate) ? candidate : null;
}
